/**
 * DocumentChangeSection - Section wrapper for document change cards.
 *
 * When blob sink is enabled, bifurcates documents into groups based on
 * BlobSinkAction status: Needs Review, In Progress, Failed, Already Pushed,
 * and Other. Each card keeps the standard Transferred/Skip buttons, with
 * sync-specific actions (Approve, Retry) added alongside.
 *
 * Sorts: created -> updated -> deleted -> ignored.
 * Uses FloatingSidePanel to render the DocumentComparisonPanel as a
 * floating utility panel to the right of the cards area.
 */

import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import Cookies from 'js-cookie';
import {
  Check,
  ClockIcon,
  Loader2,
  Upload,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { FloatingSidePanel } from '@/components/ui/FloatingSidePanel';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';
import {
  useEntityChangesBlobSinkActionsApproveMutation,
  useEntityChangesBulkUpdateStatusMutation,
} from '@/services/api/generated/entityChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { useDetailDrawer, useIsDrawerOpen } from './DetailDrawerContext';
import { DocumentChangeCard } from './DocumentChangeCard';
import { DocumentComparisonPanel } from './DocumentComparisonPanel';
import { DocumentPreviewPanel } from './DocumentPreviewPanel';

// Statuses that mean "work is happening"
const IN_PROGRESS_STATUSES = new Set([
  'PENDING',
  'PROCESSING',
  'PUSHING',
  'READY_TO_PUSH',
  'WAITING_FOR_PUSH_WINDOW',
]);

interface DocumentChangeSectionProps {
  documents: EntityChangeSchema[];
  matterId: string;
  documentEntityChangeIds: string[];
  blobSinkEnabled?: boolean;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getFilenameFromDisposition(contentDisposition: string | null): string {
  if (!contentDisposition) return 'documents.zip';
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || 'documents.zip';
}

function sortByChangeType(docs: EntityChangeSchema[]): EntityChangeSchema[] {
  return [...docs].sort(
    (a, b) =>
      CHANGE_TYPE_ORDER[a.change_type] - CHANGE_TYPE_ORDER[b.change_type],
  );
}

// ---------------------------------------------------------------------------
// Group header
// ---------------------------------------------------------------------------

const GroupHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  badgeColor: 'amber' | 'red' | 'green' | 'blue' | 'zinc';
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, label, count, badgeColor, description, action }) => (
  <div className="space-y-0.5">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <Badge color={badgeColor} className="text-xs">
          {count}
        </Badge>
      </div>
      {action}
    </div>
    {description && (
      <p className="text-xs text-gray-400 ml-6">{description}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DocumentChangeSection: React.FC<DocumentChangeSectionProps> = ({
  documents,
  matterId,
  documentEntityChangeIds,
  blobSinkEnabled,
}) => {
  const isDrawerOpen = useIsDrawerOpen();
  const { openDrawer } = useDetailDrawer();
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [approveActions] = useEntityChangesBlobSinkActionsApproveMutation();
  const [bulkUpdateStatus] = useEntityChangesBulkUpdateStatusMutation();
  const [isBulkTransferring, setIsBulkTransferring] = useState(false);

  const sorted = useMemo(() => sortByChangeType(documents), [documents]);

  const documentBlobIds = useMemo(
    () =>
      Array.from(
        new Set(
          documents
            .map((doc) => {
              const snapshot =
                doc.change_type === 'deleted'
                  ? doc.old_snapshot
                  : doc.new_snapshot;
              const data = (snapshot?.data ?? {}) as { id?: string };
              return data.id;
            })
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [documents],
  );

  // Bifurcate documents by sync status
  const { needsReview, inProgress, failed, pushed, alreadyInCms, other } =
    useMemo(() => {
      const groups = {
        needsReview: [] as EntityChangeSchema[],
        inProgress: [] as EntityChangeSchema[],
        failed: [] as EntityChangeSchema[],
        pushed: [] as EntityChangeSchema[],
        alreadyInCms: [] as EntityChangeSchema[],
        other: [] as EntityChangeSchema[],
      };

      if (!blobSinkEnabled) {
        groups.other = sorted;
        return groups;
      }

      sorted.forEach((doc) => {
        const syncInfo = doc.document_sync_info;
        const status = syncInfo?.status;
        // AWAITING_APPROVAL + already approved = approved but workflow hasn't
        // moved forward yet → treat as in-progress so the UI shows activity.
        const approvedButWaiting =
          status === 'AWAITING_APPROVAL' &&
          syncInfo?.approval_status === 'APPROVED';

        if (status === 'AWAITING_APPROVAL' && !approvedButWaiting) {
          groups.needsReview.push(doc);
        } else if (
          approvedButWaiting ||
          (status && IN_PROGRESS_STATUSES.has(status))
        ) {
          groups.inProgress.push(doc);
        } else if (status === 'FAILED') {
          groups.failed.push(doc);
        } else if (status === 'PUSHED') {
          groups.pushed.push(doc);
        } else if (status === 'ALREADY_EXISTS') {
          groups.alreadyInCms.push(doc);
        } else {
          groups.other.push(doc);
        }
      });

      return groups;
    }, [sorted, blobSinkEnabled]);

  const handleDownloadAll = useCallback(async () => {
    if (!matterId || documentBlobIds.length === 0) return;

    setDownloadError(null);
    setIsDownloadingAll(true);
    const toastId = toast.loading('Preparing files for download...', {
      description: `${documentBlobIds.length} file${documentBlobIds.length === 1 ? '' : 's'}`,
    });

    try {
      const csrftoken = Cookies.get('csrftoken');
      const response = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/blobs/download-bulk/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrftoken ? { 'X-CSRFToken': csrftoken } : {}),
          },
          body: JSON.stringify({ blob_ids: documentBlobIds }),
        },
      );

      if (!response.ok) {
        let message = `Download failed (${response.status})`;
        try {
          const errorBody = (await response.json()) as {
            error?: string;
            missing_ids?: string[];
          };
          if (errorBody.missing_ids?.length) {
            message = `${errorBody.missing_ids.length} file(s) were unavailable for download.`;
          } else if (errorBody.error) {
            message = errorBody.error;
          }
        } catch {
          // no-op: fallback message above
        }
        throw new Error(message);
      }

      const totalBytes = Number(response.headers.get('content-length') || 0);
      const contentDisposition =
        response.headers.get('Content-Disposition') ||
        response.headers.get('content-disposition');
      const filename = getFilenameFromDisposition(contentDisposition);

      if (!response.body) {
        const blob = await response.blob();
        saveAs(blob, filename);
      } else {
        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let receivedBytes = 0;

        // Stream the zip so toast can show live progress.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            receivedBytes += value.length;

            if (totalBytes > 0) {
              const percent = Math.max(
                1,
                Math.min(99, Math.round((receivedBytes / totalBytes) * 100)),
              );
              toast.loading('Downloading files...', {
                id: toastId,
                description: `${percent}% (${formatBytes(receivedBytes)} / ${formatBytes(totalBytes)})`,
              });
            } else {
              toast.loading('Downloading files...', {
                id: toastId,
                description: `${formatBytes(receivedBytes)} downloaded`,
              });
            }
          }
        }

        const blob = new Blob(chunks, {
          type: response.headers.get('content-type') || 'application/zip',
        });
        saveAs(blob, filename);
      }

      toast.success('Download ready', {
        id: toastId,
        description: `Saved ${filename}`,
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to download files. Please try again.';
      setDownloadError(message);
      toast.error('Download failed', {
        id: toastId,
        description: message,
      });
    } finally {
      setIsDownloadingAll(false);
    }
  }, [documentBlobIds, matterId]);

  const handleOpenPreview = useCallback(
    (blobId: string, name: string) => {
      openDrawer(
        <DocumentPreviewPanel
          matterId={matterId}
          blobId={blobId}
          title={name}
        />,
      );
    },
    [matterId, openDrawer],
  );

  const handleBulkApprove = useCallback(async () => {
    const actionIds = needsReview
      .map((d) => d.document_sync_info?.action_id)
      .filter((id): id is string => Boolean(id));
    if (actionIds.length === 0) return;

    try {
      const result = await approveActions({
        bulkBlobSinkActionRequestSchema: { action_ids: actionIds },
      }).unwrap();
      toast.success(
        `Approved ${result.processed_count} document${result.processed_count === 1 ? '' : 's'} for push`,
      );
    } catch {
      toast.error('Failed to approve documents');
    }
  }, [needsReview, approveActions]);

  const handleBulkTransfer = useCallback(
    async (docs: EntityChangeSchema[]) => {
      const pendingIds = docs
        .filter((d) => d.status === 'pending')
        .map((d) => d.id as string);
      if (pendingIds.length === 0) return;

      setIsBulkTransferring(true);
      try {
        await bulkUpdateStatus({
          bulkEntityChangeStatusRequestSchema: {
            entity_change_ids: pendingIds,
            action: 'transferred',
          },
        }).unwrap();
        toast.success(
          `Marked ${pendingIds.length} document${pendingIds.length === 1 ? '' : 's'} as transferred`,
        );
      } catch {
        toast.error('Failed to mark as transferred');
      } finally {
        setIsBulkTransferring(false);
      }
    },
    [bulkUpdateStatus],
  );

  if (documents.length === 0) return null;

  const hasSyncGroups =
    blobSinkEnabled &&
    (needsReview.length > 0 ||
      inProgress.length > 0 ||
      failed.length > 0 ||
      pushed.length > 0 ||
      alreadyInCms.length > 0);

  const pushedPendingCount = pushed.filter(
    (d) => d.status === 'pending',
  ).length;

  const alreadyInCmsPendingCount = alreadyInCms.filter(
    (d) => d.status === 'pending',
  ).length;

  // Render a list of DocumentChangeCards, passing syncVariant so the card
  // can render inline Push/Reject/Retry actions next to the download button.
  const renderCards = (
    docs: EntityChangeSchema[],
    syncVariant?: 'needs_review' | 'failed',
  ) =>
    docs.map((doc) => (
      <DocumentChangeCard
        key={doc.id}
        entity={doc}
        matterId={matterId}
        onOpenPreview={handleOpenPreview}
        syncVariant={syncVariant}
      />
    ));

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Documents</h3>
            <Badge color="zinc" className="text-xs">
              {documents.length}
            </Badge>
          </div>

          <button
            type="button"
            onClick={handleDownloadAll}
            disabled={isDownloadingAll || documentBlobIds.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {isDownloadingAll ? 'Preparing download...' : 'Download all files'}
          </button>
        </div>
        {downloadError && (
          <p className="text-xs text-red-600">{downloadError}</p>
        )}
      </div>

      {/* Sync-disabled banner */}
      {!blobSinkEnabled && (
        <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2">
          <InformationCircleIcon className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-600">
            Document sync can automate pushing documents to your CMS. Contact
            your admin to enable it.
          </p>
        </div>
      )}

      <FloatingSidePanel
        hidden={isDrawerOpen}
        panel={
          <DocumentComparisonPanel
            matterId={matterId}
            documentEntityChangeIds={documentEntityChangeIds}
          />
        }
      >
        {hasSyncGroups ? (
          <div className="space-y-6">
            {/* Needs Review */}
            {needsReview.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={<ClockIcon className="h-4 w-4 text-amber-500" />}
                  label="Needs Review"
                  count={needsReview.length}
                  badgeColor="amber"

                  action={
                    needsReview.length > 1 ? (
                      <button
                        type="button"
                        onClick={handleBulkApprove}
                        className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Push all
                      </button>
                    ) : undefined
                  }
                />
                {renderCards(needsReview, 'needs_review')}
              </div>
            )}

            {/* In Progress */}
            {inProgress.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                  }
                  label="Pushing"
                  count={inProgress.length}

                  badgeColor="blue"
                />
                {renderCards(inProgress)}
              </div>
            )}

            {/* Failed */}
            {failed.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                  }
                  label="Push Failed"
                  count={failed.length}

                  badgeColor="red"
                />
                {renderCards(failed, 'failed')}
              </div>
            )}

            {/* Already Pushed */}
            {pushed.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={<CheckCircleIcon className="h-4 w-4 text-green-500" />}
                  label="Already Pushed"
                  count={pushed.length}
                  badgeColor="green"

                  action={
                    pushedPendingCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleBulkTransfer(pushed)}
                        disabled={isBulkTransferring}
                        className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 disabled:opacity-50"
                      >
                        {isBulkTransferring ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" />
                        )}
                        Mark all as transferred
                      </button>
                    ) : undefined
                  }
                />
                {renderCards(pushed)}
              </div>
            )}

            {/* Already in CMS */}
            {alreadyInCms.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={<CheckBadgeIcon className="h-4 w-4 text-blue-400" />}
                  label="Already in CMS"
                  count={alreadyInCms.length}
                  badgeColor="blue"

                  action={
                    alreadyInCmsPendingCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleBulkTransfer(alreadyInCms)}
                        disabled={isBulkTransferring}
                        className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 disabled:opacity-50"
                      >
                        {isBulkTransferring ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" />
                        )}
                        Mark all as transferred
                      </button>
                    ) : undefined
                  }
                />
                {renderCards(alreadyInCms)}
              </div>
            )}

            {/* Other / no sync info */}
            {other.length > 0 && (
              <div className="space-y-2">
                <GroupHeader
                  icon={<ArrowPathIcon className="h-4 w-4 text-gray-400" />}
                  label="Other"
                  count={other.length}
                  badgeColor="zinc"

                />
                {renderCards(other)}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((doc) => (
              <DocumentChangeCard
                key={doc.id}
                entity={doc}
                matterId={matterId}
                onOpenPreview={handleOpenPreview}
              />
            ))}
          </div>
        )}
      </FloatingSidePanel>
    </div>
  );
};
